/**
 * Belépési pont és játék-orkesztráció.
 *
 * Felelőssége szándékosan szűk: állapotgép (cím / játék / szünet / halál /
 * győzelem), pályabetöltés, a rendszerek sorrendje, és a hurok. Minden más
 * a saját moduljában lakik.
 *
 * Gazda: C
 */

import * as THREE from 'three'
import './ui/style.css'

import { TICK_DT, type Ctx, type GameEventMap, type HudState, type System, type Actor } from './core/contracts'
import { EventBus } from './core/events'
import { Rng } from './core/rng'
import { GameLoop } from './core/loop'
import { ActorStore } from './core/actors'
import { World } from './world/world'
import type { LevelDef } from './world/level'
import { Renderer } from './render/renderer'
import { Fx } from './render/fx'
import { Hud } from './ui/hud'
import { Screen, SCREENS } from './ui/menu'
import { AudioEngine } from './assets/audio'
import { generateAll, TEXTURE_IDS } from './assets/textures'
import { Input } from './input/input'
import { PlayerSystem, PLAYER } from './player/player'
import { CombatSystem } from './combat/combat'
import { EnemySystem } from './enemies/enemies'
import { ENEMIES } from './content/enemies'
import { LEVEL_1 } from './content/levels/l1-behatolas'
import { LEVEL_2 } from './content/levels/l2-olvasztomu'
import { LEVEL_3 } from './content/levels/l3-magkamra'

const CAMPAIGN: LevelDef[] = [LEVEL_1, LEVEL_2, LEVEL_3]

interface ExitMarker {
  x: number
  z: number
}

/** Egy egérzár-esemény pillanatfelvétele az F3 hibajelentéséhez. */
interface PointerLockDiagnostic {
  time: string
  change: string
  state: string
  frameMs: number
  gameEvents: string
}

class Game {
  private canvas: HTMLCanvasElement
  private events = new EventBus<GameEventMap>()
  private rng = new Rng(0x1d0043)
  private actors = new ActorStore(this.events)
  private audio = new AudioEngine()
  private hud: Hud
  private screen: Screen
  private renderer: Renderer
  private input: Input
  private fx: Fx
  private loop: GameLoop
  private debugEl: HTMLElement
  private debugCopyEl: HTMLButtonElement

  private world: World | null = null
  private player: PlayerSystem | null = null
  private systems: System[] = []
  private exits: ExitMarker[] = []
  private levelIndex = 0
  private levelStartTick = 0
  private stats = { kills: 0, secrets: 0, deaths: 0 }
  private debugVisible = false
  /** Csak egyszer figyelmeztessünk az egérzár hiányára. */
  private lockWarned = false
  /** Volt-e egérzár az előző kereten (a védőhálóhoz). */
  private wasLocked = false
  /** Az F3 csak a legutóbbi, releváns harci eseményeket mutatja. */
  private recentGameEvents: string[] = []
  /** Körkörös napló: egy hibás zárváltás után is megmarad az előzménye. */
  private pointerLockDiagnostics: PointerLockDiagnostic[] = []
  /**
   * A kampányon átvitt készlet. A `PlayerSystem` pályánként újra jön létre
   * (a `World` referenciája miatt), ezért a fegyverek/lőszer/páncél
   * megőrzése ITT a feladat — enélkül minden pálya elején elveszne a
   * sörétes, ami a GDD 1. pontját sértené („a fegyver megmarad a
   * kampányon belül").
   */
  private carried: {
    ammo: Record<string, number>
    weapons: string[]
    armor: number
    health: number
  } | null = null

  private hudState: HudState = {
    health: 100,
    maxHealth: 100,
    armor: 0,
    ammo: 0,
    ammoMax: 0,
    ammoType: 'bullet',
    weaponId: 'pistol',
    weaponName: 'PISZTOLY',
    weapons: [],
    keys: [],
    enemiesTotal: 0,
    enemiesLeft: 0,
    secretsFound: 0,
    secretsTotal: 0,
    fireProgress: 1,
    regenActive: false,
  }

  private ctx: Ctx

  constructor(root: HTMLElement) {
    this.canvas = document.createElement('canvas')
    this.canvas.id = 'view'
    root.appendChild(this.canvas)

    this.hud = new Hud(root)
    this.hud.setVisible(false)
    this.screen = new Screen(root)

    this.debugEl = document.createElement('div')
    this.debugEl.className = 'debug hidden'
    root.appendChild(this.debugEl)

    // Másolás gomb a diagnosztikához. Enélkül a hibajelentés kézzel
    // olvasható csak le a képernyőről, ami hosszú naplónál használhatatlan.
    // A `C` billentyű ugyanezt teszi, mert egérzár közben a gombra
    // kattintani nem lehet.
    this.debugCopyEl = document.createElement('button')
    this.debugCopyEl.className = 'debug-copy hidden'
    this.debugCopyEl.textContent = 'Diagnosztika másolása  ( C )'
    this.debugCopyEl.addEventListener('click', (e) => {
      e.stopPropagation()
      void this.copyDiagnostics()
    })
    root.appendChild(this.debugCopyEl)


    this.renderer = new Renderer(this.canvas, this.events, this.rng)
    this.input = new Input(this.canvas)
    this.fx = new Fx(this.renderer, this.audio, this.hud)

    this.ctx = {
      dt: TICK_DT,
      tick: 0,
      rng: this.rng,
      // A világ a pályabetöltéskor jön létre; addig szándékosan hibát dob,
      // ha valaki idő előtt hozzányúlna.
      get world() {
        throw new Error('Nincs betöltött pálya.')
      },
      actors: this.actors,
      fx: this.fx,
      events: this.events,
      input: this.input.state,
      hud: this.hudState,
      view: this.renderer.viewModel,
      camera: { pos: new THREE.Vector3(), yaw: 0, pitch: 0, roll: 0, fov: 78 },
      state: 'title',
      levelIndex: 0,
    } as unknown as Ctx

    this.loop = new GameLoop(
      () => this.fixedUpdate(),
      (alpha, dtReal) => this.render(alpha, dtReal),
    )

    this.wireInput()
    this.wireEvents()
    this.wirePointerLockDiagnostics()
  }

  // -------------------------------------------------------------------------
  // Bekötések
  // -------------------------------------------------------------------------

  private wireInput(): void {
    this.input.onEscape = () => {
      if (this.ctx.state === 'playing') this.pause()
      else if (this.ctx.state === 'paused') this.resume()
    }
    this.input.onConfirm = () => this.screen.trigger()

    // Ha az egérzár nem kérhető (nem biztonságos kontextus: sima HTTP nem
    // localhost hoszton, vagy beágyazott keret), a játékos nem tud egérrel
    // nézelődni, és a kurzor „kiszökik" az ablakból. Ilyenkor nem hagyjuk
    // szó nélkül: megmondjuk, mi a helyzet, és hogy mivel lehet fordulni.
    this.input.onLockFailed = () => {
      this.recordPointerLockChange('zárkérés elutasítva')
      if (this.lockWarned) return
      this.lockWarned = true
      document.body.classList.add('nolock')
      // Csak EGY rövid üzenet, ami magától eltűnik. Ide már csak akkor
      // jutunk, ha három egymást követő zárkérés is elbukott — vagyis
      // tényleg nincs egérzár, nem csak átmeneti elutasítás történt.
      this.fx.message('Nincs egérzár — fordulás: ← → vagy Q / E')
    }
    // A diagnosztika kapcsolását SZÁNDÉKOSAN nem az `Input`-on keresztül
    // kötjük be, hanem lentebb, ablak szinten. Ha mindkettő élne, az F3
    // kétszer kapcsolna — oda-vissza —, és semmi nem történne. (Pontosan
    // ez volt a hiba.) Az `Input.onToggleDebug` így bekötetlen marad.
    this.input.onToggleDebug = null
    this.input.onCopyDiagnostics = () => void this.copyDiagnostics()

    // Saját, ablak-szintű billentyűfigyelő a diagnosztikához.
    //
    // Szándékosan NEM az `Input` osztályon keresztül: a diagnosztikának
    // akkor is működnie kell, amikor a bemenetkezelés bármiért nem fut
    // (szünet, elveszett egérzár, hibaállapot) — pont azokban a
    // helyzetekben van rá a legnagyobb szükség.
    window.addEventListener(
      'keydown',
      (e) => {
        if (e.repeat) return
        if (e.code === 'Backquote' || e.code === 'F3') {
          e.preventDefault()
          this.debugVisible = !this.debugVisible
          this.debugEl.classList.toggle('hidden', !this.debugVisible)
          this.debugCopyEl.classList.toggle('hidden', !this.debugVisible)
        } else if (e.code === 'KeyC') {
          e.preventDefault()
          void this.copyDiagnostics()
        }
      },
      true,
    )

    /**
     * Felhasználói kattintás kezelése.
     *
     * A SORREND kritikus. A böngésző az egérzárat csak a felhasználói
     * gesztus rövid érvényességi ablakában engedi. Ha előbb elindítjuk a
     * hangmotort (ami ~30 effektet szintetizál) és betöltjük a pályát
     * (geometria-építés, aktorok), az ablak lejárhat, és a zárkérés
     * csendben elbukik — a kurzor „kiszökik" a játékból. Ezért a zárat
     * KÉRJÜK ELŐSZÖR, és csak utána dolgozunk.
     */
    const onActivate = (): void => {
      if (!this.input.locked) this.input.requestLock()
      this.audio.ensureStarted()
      if (this.screen.visible) this.screen.trigger()
    }
    this.canvas.addEventListener('click', onActivate)
    // A képernyőkre is lehet kattintani, ne csak a canvasra.
    this.screen.root.addEventListener('click', onActivate)

    document.addEventListener('pointerlockchange', () => {
      this.recordPointerLockChange(this.input.locked ? 'zár aktív' : 'zár feloldva')
      // A pointer lock elvesztése (Esc, tabváltás) mindig szünetet jelent —
      // nem futhat a játék úgy, hogy a játékos nem tud célozni.
      if (!this.input.locked && this.ctx.state === 'playing') this.pause()
    })
    document.addEventListener('contextmenu', (e) => e.preventDefault())
  }

  /**
   * A WebGL-kontekztus elvesztése tipikusan a canvashoz kötött egérzár
   * megszűnésével jár. A Three.js vissza tudja építeni a kontextust, ha az
   * eseményt nem hagyjuk a böngésző alapértelmezett, végleges hibautjára.
   * Itt nem próbálunk automatikusan visszazárni: ahhoz új felhasználói
   * gesztus kell. A szünetképernyő egy kattintása biztonságosan folytatja.
   */
  private wirePointerLockDiagnostics(): void {
    this.canvas.addEventListener('webglcontextlost', (event) => {
      event.preventDefault()
      this.recordGameEvent('WebGL-kontekztus elveszett')
      this.recordPointerLockChange('WebGL-kontekztus elveszett')
      if (this.ctx.state === 'playing') this.pause()
    })
    this.canvas.addEventListener('webglcontextrestored', () => {
      this.recordGameEvent('WebGL-kontekztus helyreállt')
      this.recordPointerLockChange('WebGL-kontekztus helyreállt')
      this.fx.message('A grafikus kontextus helyreállt — kattints a folytatáshoz')
    })

    // A robbanásnak nincs saját EventBus-eseménye. A diagnosztika kedvéért
    // itt, a tulajdonunkban lévő főorkesztrátorban megfigyeljük az Fx-hívást;
    // ezzel a harci/Fx-modul viselkedése és publikus szerződése változatlan.
    const explosion = this.fx.explosion.bind(this.fx)
    this.fx.explosion = (point, radius, color) => {
      this.recordGameEvent(`robbanás (r=${radius.toFixed(1)})`)
      explosion(point, radius, color)
    }
  }

  private recordGameEvent(event: string): void {
    this.recentGameEvents.push(event)
    if (this.recentGameEvents.length > 3) this.recentGameEvents.shift()
  }

  private recordPointerLockChange(change: string): void {
    this.pointerLockDiagnostics.push({
      time: new Date().toISOString().slice(11, 23) + 'Z',
      change,
      state: this.ctx.state,
      frameMs: this.loop.smoothedFrameTime * 1000,
      gameEvents: this.recentGameEvents.length > 0 ? this.recentGameEvents.join(' | ') : 'nincs',
    })
    if (this.pointerLockDiagnostics.length > 10) this.pointerLockDiagnostics.shift()
  }

  private wireEvents(): void {
    this.events.on('death', ({ actor, source }) => {
      this.recordGameEvent(`halál: ${actor.kind}/${actor.type}`)
      if (actor.kind === 'enemy') {
        this.stats.kills++
        const arch = ENEMIES[actor.type]
        this.fx.sound(arch?.sounds.death ?? 'enemyDeath', actor.pos, 0.9)
        this.fx.gib(
          new THREE.Vector3(actor.pos.x, actor.pos.y + actor.height * 0.55, actor.pos.z),
          arch?.gibColor ?? 0xa53347,
          14,
        )
      }
      void source
    })

    this.events.on('damage', ({ target, amount, source, kind }) => {
      this.recordGameEvent(`sebzés: ${target.kind}/${target.type} -${amount} (${kind}, ${source?.type ?? 'környezet'})`)
      if (target.kind === 'player') this.player?.onDamaged(this.ctx)
    })

    this.events.on('secretFound', () => {
      this.stats.secrets++
      this.hudState.secretsFound++
      this.fx.sound('secret', null, 0.8)
      this.fx.message('Titkos helyiség!')
    })

    this.events.on('playerDied', ({ by }) => {
      this.stats.deaths++
      const arch = ENEMIES[by]
      window.setTimeout(() => {
        if (this.ctx.state !== 'dead') return
        this.input.releaseLock()
        this.screen.show(SCREENS.death(arch?.name ?? 'a bázis'), () => this.loadLevel(this.levelIndex))
      }, 1400)
    })
  }

  // -------------------------------------------------------------------------
  // Indulás
  // -------------------------------------------------------------------------

  async boot(): Promise<void> {
    this.screen.show(SCREENS.loading())
    // A textúra-generálás kereten át fut, hogy a betöltősáv valóban mozogjon
    // és a böngésző ne fagyjon le egy hosszú blokkoló ciklusra.
    const gen = generateAll()
    let done = 0
    await new Promise<void>((resolve) => {
      const step = (): void => {
        const t0 = performance.now()
        // Kereten belül annyit generálunk, amennyi 24 ms-be belefér.
        while (performance.now() - t0 < 24) {
          const next = gen.next()
          if (next.done) {
            resolve()
            return
          }
          done++
        }
        this.screen.setProgress(done / TEXTURE_IDS.length)
        requestAnimationFrame(step)
      }
      requestAnimationFrame(step)
    })
    this.screen.setProgress(1)

    this.screen.show(SCREENS.title(), () => this.startCampaign())
    this.loop.start()
  }

  private startCampaign(): void {
    this.stats = { kills: 0, secrets: 0, deaths: 0 }
    // Új kampány: tiszta lappal, az alapfelszereléssel.
    this.carried = null
    this.loadLevel(startLevelIndex())
  }

  // -------------------------------------------------------------------------
  // Pályabetöltés
  // -------------------------------------------------------------------------

  private loadLevel(index: number): void {
    this.levelIndex = index
    const def = CAMPAIGN[index]

    this.renderer.unloadLevel()
    this.world?.dispose()
    this.actors.clear()
    this.exits.length = 0

    const world = new World(def, this.events)
    this.world = world
    world.actorsRef = this.actors.all()
    this.renderer.loadLevel(def, world.group)

    // A `Ctx.world` getterét a friss világra kötjük.
    Object.defineProperty(this.ctx, 'world', { value: world, configurable: true, writable: false })
    this.ctx.levelIndex = index
    this.ctx.tick = 0
    this.levelStartTick = 0

    // --- Dolgok kihelyezése ---
    let playerStart = { x: 0, z: 0, yaw: 0 }
    let enemyCount = 0

    for (const t of def.things) {
      if (t.kind === 'playerStart') {
        playerStart = { x: t.pos.x, z: t.pos.z, yaw: t.yaw ?? 0 }
      } else if (t.kind === 'exit') {
        this.exits.push({ x: t.pos.x, z: t.pos.z })
      }
    }

    const player = this.actors.spawn({
      kind: 'player',
      type: 'player',
      faction: 'player',
      x: playerStart.x,
      y: world.floorAt(playerStart.x, playerStart.z),
      z: playerStart.z,
      yaw: playerStart.yaw,
      radius: PLAYER.radius,
      height: PLAYER.height,
      eyeHeight: PLAYER.eyeHeight,
      health: 100,
    })
    player.roomId = world.roomAt(player.pos.x, player.pos.z)

    for (const t of def.things) {
      if (t.kind === 'enemy') {
        const arch = ENEMIES[t.type]
        if (!arch) {
          console.warn(`Ismeretlen ellenségtípus a pályán: ${t.type}`)
          continue
        }
        const a = this.actors.spawn({
          kind: 'enemy',
          type: t.type,
          faction: 'enemy',
          x: t.pos.x,
          y: world.floorAt(t.pos.x, t.pos.z),
          z: t.pos.z,
          yaw: t.yaw ?? 0,
          radius: arch.radius,
          height: arch.height,
          eyeHeight: arch.eyeHeight,
          health: arch.health,
          // Az encounter-csoport a pályaadatból: egy csoporttag észlelése
          // a többit is felriasztja (X kérése).
          data: t.group ? { group: t.group } : undefined,
        })
        a.roomId = world.roomAt(a.pos.x, a.pos.z)
        enemyCount++
      } else if (t.kind === 'pickup') {
        const a = this.actors.spawn({
          kind: 'pickup',
          type: t.type,
          faction: 'neutral',
          x: t.pos.x,
          y: world.floorAt(t.pos.x, t.pos.z),
          z: t.pos.z,
          radius: 0.4,
          height: 0.8,
          solid: false,
          vulnerable: false,
        })
        a.roomId = world.roomAt(a.pos.x, a.pos.z)
      }
    }

    // --- Rendszerek ---
    for (const s of this.systems) s.dispose?.()
    const playerSystem = new PlayerSystem(world, this.input)
    this.player = playerSystem
    this.actors.armorHook = playerSystem.armorHook
    // A területi sebzés innentől takarás-érzékeny: a fedezék mögé húzódó
    // testet a robbanás nem éri el.
    this.actors.lineOfSight = (from, to) => world.lineOfSight(from, to)

    // A sorrend számít (X kérése a 07-X-atadas.md-ben): a harci rendszer
    // lépteti a MÁR MEGLÉVŐ lövedékeket, az ellenfél-rendszer pedig utána
    // hoz létre újakat. Fordított sorrendben egy frissen kilőtt lövedék
    // ugyanabban a tickben lépne egyet, ami fél tick előnyt adna az AI-nak.
    this.systems = [playerSystem, new CombatSystem(playerSystem), new EnemySystem()]

    // Készlet visszatöltése az előző pályáról. A KULCSOK szándékosan nem
    // öröklődnek: minden pálya saját zárrendszerrel dolgozik.
    if (this.carried) {
      const inv = playerSystem.inventory
      for (const [kind, amount] of Object.entries(this.carried.ammo)) {
        if (kind in inv.ammo) inv.ammo[kind as keyof typeof inv.ammo] = amount
      }
      inv.weapons = new Set(this.carried.weapons)
      inv.armor = this.carried.armor
      inv.health = this.carried.health
    }

    // Az élet a pályák között átvihető: a GDD szerint belépéskor min. 70.
    playerSystem.inventory.health = Math.max(70, playerSystem.inventory.health)
    player.health = playerSystem.inventory.health
    player.maxHealth = playerSystem.inventory.maxHealth

    for (const s of this.systems) s.init?.(this.ctx)

    // --- HUD alapállapot ---
    this.hudState.enemiesTotal = enemyCount
    this.hudState.enemiesLeft = enemyCount
    this.hudState.secretsTotal = world.secretsTotal
    this.hudState.secretsFound = 0

    // Shader-előmelegítés. Csak ITT, a betöltés legvégén van értelme:
    // ekkorra minden aktor létrejött és a kézifegyver is fel van szerelve.
    // Enélkül az első lövés/becsapódás/nyomjelző a HARC KÖZBEN fordítaná le
    // a shaderét, több száz milliszekundumos főszál-blokkolással — amitől a
    // böngésző az egérzárat is elengedi. (X `19-X-revizio-2.md` F1.)
    this.renderer.prewarm()

    this.ctx.state = 'playing'
    this.hud.setVisible(true)
    this.screen.hide()
    this.audio.ensureStarted()
    this.audio.startMusic()
    // Ha a kattintáskori kérés valamiért nem ment át, itt még egyszer
    // próbálkozunk — de ez már a gesztus-ablakon kívül eshet.
    if (!this.input.locked) this.input.requestLock()
    this.fx.message(`${def.name.toUpperCase()} — ${def.subtitle ?? ''}`)
  }

  // -------------------------------------------------------------------------
  // Állapot
  // -------------------------------------------------------------------------

  private pause(): void {
    if (this.ctx.state !== 'playing') return
    this.ctx.state = 'paused'
    document.body.classList.add('paused')
    this.input.releaseLock()
    this.audio.setThreat(0)
    this.screen.show(SCREENS.paused(), () => this.resume())
  }

  private resume(): void {
    if (this.ctx.state !== 'paused') return
    this.ctx.state = 'playing'
    document.body.classList.remove('paused')
    this.screen.hide()
    // A kattintás-kezelő már kérte a zárat; itt csak akkor próbálkozunk,
    // ha valamiért mégsem sikerült.
    if (!this.input.locked) this.input.requestLock()
  }

  private completeLevel(): void {
    if (this.ctx.state !== 'playing') return
    this.ctx.state = 'won'
    this.input.releaseLock()
    this.audio.setThreat(0)
    this.fx.sound('exitLevel', null, 0.9)

    const def = CAMPAIGN[this.levelIndex]
    const seconds = Math.floor((this.ctx.tick - this.levelStartTick) * TICK_DT)
    const stats = [
      { key: 'IDŐ', value: `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}` },
      { key: 'ELLENSÉG', value: `${this.hudState.enemiesTotal - this.hudState.enemiesLeft}/${this.hudState.enemiesTotal}` },
      { key: 'TITKOK', value: `${this.hudState.secretsFound}/${Math.max(1, this.hudState.secretsTotal)}` },
    ]

    // A pálya teljesítésekor mentjük a készletet a következőre.
    if (this.player) {
      const inv = this.player.inventory
      this.carried = {
        ammo: { ...inv.ammo },
        weapons: [...inv.weapons],
        armor: inv.armor,
        health: inv.health,
      }
    }

    const isLast = this.levelIndex >= CAMPAIGN.length - 1
    this.hud.setVisible(false)
    if (isLast) {
      this.screen.show(SCREENS.victory(stats), () => this.startCampaign())
    } else {
      this.screen.show(SCREENS.levelDone(def.name, stats, false), () => this.loadLevel(this.levelIndex + 1))
    }
  }

  // -------------------------------------------------------------------------
  // Hurok
  // -------------------------------------------------------------------------

  private fixedUpdate(): void {
    const state = this.ctx.state
    if (state !== 'playing' && state !== 'dead') {
      // Szünetben és képernyőkön a bemenetet is nullázni kell, különben
      // a visszatéréskor egy tickre beugrana a felgyűlt egérmozgás.
      this.input.beginTick()
      this.input.state.lookYaw = 0
      this.input.state.lookPitch = 0
      return
    }

    this.input.beginTick()
    this.actors.snapshotPrev()
    this.ctx.tick++

    for (const s of this.systems) s.fixedUpdate(this.ctx)

    this.world?.update(TICK_DT, this.actors.all())

    if (state === 'playing') {
      this.updateProgress()
      this.checkExit()
    }
  }

  private updateProgress(): void {
    let alive = 0
    let threat = 0
    const player = this.actors.player
    for (const a of this.actors.all()) {
      if (a.kind !== 'enemy') continue
      if (!a.alive) continue
      alive++
      // A zene fenyegetettsége a közeli, élő ellenfelek számából jön.
      const d = Math.hypot(a.pos.x - player.pos.x, a.pos.z - player.pos.z)
      if (d < 26) threat += 1 - d / 26
    }
    this.hudState.enemiesLeft = alive
    this.audio.setThreat(Math.min(1, threat / 3.5))

    // A hullák egy idő után eltűnnek, hogy ne fogyjon a keret.
    for (const a of this.actors.all()) {
      if (!a.alive && a.kind === 'enemy' && a.deadFor > 60 * 25) {
        this.actors.remove(a.id)
        break
      }
    }
  }

  private checkExit(): void {
    if (this.exits.length === 0) return
    const p = this.actors.player
    if (!p.alive) return
    for (const e of this.exits) {
      if (Math.hypot(p.pos.x - e.x, p.pos.z - e.z) < 1.8) {
        this.completeLevel()
        return
      }
    }
  }

  /**
   * Védőháló az egérzár elvesztésére.
   *
   * A `pointerlockchange` eseményre már feliratkoztunk, de ha az bármiért
   * elmaradna (böngészőhiba, elveszett esemény), a játék zár nélkül futna
   * tovább: a játékos nem tudna célozni, az ellenfél viszont lőne. Ezért
   * keretenként is ellenőrizzük az állapotot. Egyszerű, olcsó, és
   * garantálja, hogy soha ne fusson „vakon" a szimuláció.
   */
  private guardPointerLock(): void {
    if (this.ctx.state !== 'playing') {
      this.wasLocked = this.input.locked
      return
    }
    if (this.wasLocked && !this.input.locked) {
      this.wasLocked = false
      this.pause()
      return
    }
    this.wasLocked = this.input.locked
  }

  /**
   * A teljes diagnosztika vágólapra másolása.
   *
   * A `navigator.clipboard` biztonságos kontextust és fókuszt igényel;
   * ha bármelyik hiányzik, visszaesünk egy rejtett textarea + `execCommand`
   * megoldásra, hogy a hibajelentés mindenképp kimásolható legyen.
   */
  private async copyDiagnostics(): Promise<void> {
    const text = [
      `Alászállás a mélybe — diagnosztika`,
      `idő: ${new Date().toISOString()}`,
      `böngésző: ${navigator.userAgent}`,
      `felbontás: ${window.innerWidth}×${window.innerHeight} @${window.devicePixelRatio}`,
      `biztonságos kontextus: ${window.isSecureContext}`,
      '',
      this.debugEl.textContent ?? '',
    ].join('\n')

    let ok = false
    try {
      await navigator.clipboard.writeText(text)
      ok = true
    } catch {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      try {
        ok = document.execCommand('copy')
      } catch {
        ok = false
      }
      ta.remove()
    }
    this.fx.message(ok ? 'Diagnosztika a vágólapon' : 'A másolás nem sikerült')
  }

  private render(alpha: number, dtReal: number): void {
    this.guardPointerLock()
    const p = this.actors.all().length > 0 ? this.tryPlayer() : null

    if (this.world && p) {
      this.renderer.syncActors(
        this.actors.all(),
        alpha,
        dtReal,
        (x, z, hint) => this.world!.floorAt(x, z, hint),
        (x, z, hint) => this.world!.hazardAt(x, z, hint),
      )
      this.audio.setListener(this.ctx.camera.pos, this.ctx.camera.yaw)
      this.renderer.viewModel.update(
        dtReal,
        Math.hypot(p.vel.x, p.vel.z),
        p.onGround,
        this.player?.lastLookDelta.x ?? 0,
        this.player?.lastLookDelta.y ?? 0,
      )
      this.hud.update(this.hudState, dtReal)
    }

    const hurt = p && p.maxHealth > 0 ? Math.max(0, 1 - (p.health / p.maxHealth) / 0.3) : 0
    this.renderer.render(this.ctx.camera, dtReal, Math.min(1, hurt))

    if (this.debugVisible) this.updateDebug()
  }

  private tryPlayer(): Actor | null {
    try {
      return this.actors.player
    } catch {
      return null
    }
  }

  private updateDebug(): void {
    const p = this.tryPlayer()
    const lines = [
      `FPS       ${this.loop.fps.toFixed(0)}`,
      `TICK      ${this.ctx.tick}`,
      `AKTOR     ${this.actors.all().length}`,
      `EGÉRZÁR   ${this.input.locked ? 'aktív' : 'nincs'}`,
      `BIZT.KTX  ${window.isSecureContext ? 'igen' : 'NEM (http)'}`,
      `DRAW      ${this.renderer.sceneStats.calls}`,
      `HÁROMSZÖG ${this.renderer.sceneStats.triangles}`,
    ]
    if (p) {
      lines.push(
        `POZ       ${p.pos.x.toFixed(1)} ${p.pos.y.toFixed(1)} ${p.pos.z.toFixed(1)}`,
        `SZOBA     ${p.roomId}`,
        `SEBESSÉG  ${Math.hypot(p.vel.x, p.vel.z).toFixed(1)}`,
      )
    }
    lines.push('', 'EGÉRZÁR-NAPLÓ (utolsó 10)')
    if (this.pointerLockDiagnostics.length === 0) {
      lines.push('  még nincs zárváltozás')
    } else {
      for (const entry of this.pointerLockDiagnostics) {
        lines.push(
          `  ${entry.time}  ${entry.change} | állapot: ${entry.state} | ${entry.frameMs.toFixed(1)} ms`,
          `    események: ${entry.gameEvents}`,
        )
      }
    }
    this.debugEl.textContent = lines.join('\n')
  }
}

// ---------------------------------------------------------------------------

/**
 * `?level=2` — a kampány közepéről indít. Fejlesztői és tesztelési kényelem:
 * enélkül a 3. pálya ellenőrzéséhez végig kellene játszani az első kettőt.
 * A kampány-készlet ilyenkor is az alapfelszerelés marad.
 */
function startLevelIndex(): number {
  const raw = new URLSearchParams(window.location.search).get('level')
  if (raw === null) return 0
  const n = Number.parseInt(raw, 10)
  if (!Number.isFinite(n)) return 0
  return Math.max(0, Math.min(CAMPAIGN.length - 1, n - 1))
}

const root = document.getElementById('app')
if (!root) throw new Error('Hiányzik a #app elem.')

const game = new Game(root)
void game.boot()
